import React from "react";
import { useLocation, Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/contexts/AuthContext";
import LanguageSwitcher from "@/components/common/LanguageSwitcher";
import { ROUTE_I18N_KEYS, REPORT_I18N_KEYS } from "@/lib/routeLabels";
import { Bell, ChevronDown, HelpCircle, Calendar } from "lucide-react";
import GlobalSearch from "./GlobalSearch";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
import { Badge } from "@/components/ui/badge";
import BranchCounterSelectors from "./BranchCounterSelectors";
import { REPORT_CENTER_ITEMS } from "@/lib/reportCenterNav";
import { useOnlineOrderInbox } from "@/hooks/useOnlineOrderInbox";
import { formatStatus } from "@/components/online/OnlineOrderDetailSheet";
import { cn } from "@/lib/utils";

const REPORT_ROUTE_LABELS = Object.fromEntries(
  REPORT_CENTER_ITEMS.map((r) => [r.path, r.label]),
);

function getCrumbs(pathname, t) {
  if (pathname === "/") return [{ label: t("nav.dashboard"), to: "/" }];
  const segs = pathname.split("/").filter(Boolean);
  const crumbs = [{ label: t("common.home"), to: "/" }];
  let acc = "";
  segs.forEach((s) => {
    acc += "/" + s;
    const key = ROUTE_I18N_KEYS[acc] || REPORT_I18N_KEYS[acc];
    crumbs.push({
      label: key ? t(key) : REPORT_ROUTE_LABELS[acc] || s.replace(/-/g, " "),
      to: acc,
    });
  });
  return crumbs;
}

function initials(name) {
  if (!name) return "?";
  return name
    .split(" ")
    .map((part) => part[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();
}

function timeAgo(iso) {
  if (!iso) return "";
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.max(0, Math.floor(diff / 60000));
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins} min ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return new Date(iso).toLocaleDateString();
}

export default function TopHeader() {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const crumbs = getCrumbs(pathname, t);
  const { counts, newOrders, pendingDelivery, badgeTotal } = useOnlineOrderInbox();

  const counterLabel = user?.default_counter
    ? `${user.default_counter.name} • ${user.default_counter.device_code || "POS"}`
    : t("common.noCounter");
  const branchLabel = user?.default_branch?.name || t("common.noBranch");

  async function handleLogout() {
    await logout();
    navigate("/login", { replace: true });
  }

  const notifications = [
    ...newOrders.map((o) => ({
      id: `new-${o.id}`,
      title: `${t("onlineOrders.newOrders")}: ${o.reference} · ${o.customer_name}`,
      time: timeAgo(o.placed_at),
      type: "info",
      to: `/online/orders?order=${o.id}&attention=1`,
    })),
    ...pendingDelivery.map((o) => ({
      id: `del-${o.id}`,
      title: `${t("onlineOrders.pendingDelivery")}: ${o.reference} · ${formatStatus(o.status)}`,
      time: timeAgo(o.placed_at),
      type: "warn",
      to: `/online/orders?order=${o.id}&attention=1`,
    })),
  ].slice(0, 12);

  return (
    <header
      data-testid="app-header"
      className="h-14 flex items-center px-2 sm:px-4 app-glass gap-2 sm:gap-3 sticky top-0 z-30 min-w-0 overflow-hidden"
    >
      <nav
        data-testid="breadcrumbs"
        className="flex items-center text-[12px] sm:text-[13px] text-brand-brown/60 min-w-0 flex-1 overflow-hidden"
      >
        {crumbs.map((c, i) => {
          const isLast = i === crumbs.length - 1;
          const hideOnMobile = !isLast && i < crumbs.length - 1;
          return (
            <React.Fragment key={c.to}>
              {i > 0 && (
                <span className={cn("mx-1.5 text-slate-300", hideOnMobile && "hidden sm:inline")}>
                  /
                </span>
              )}
              {isLast ? (
                <span className="text-brand-brown font-medium truncate">{c.label}</span>
              ) : (
                <Link
                  className={cn("hover:text-slate-700 truncate", hideOnMobile && "hidden sm:inline")}
                  to={c.to}
                >
                  {c.label}
                </Link>
              )}
            </React.Fragment>
          );
        })}
      </nav>

      <div className="ml-auto flex items-center gap-1 sm:gap-2 shrink-0">
        <BranchCounterSelectors />
        <GlobalSearch />

        <div className="hidden lg:flex items-center gap-2 h-9 px-3 rounded-xl bg-gradient-to-r from-emerald-50/80 to-amber-50/50 border border-slate-200/80 text-[12px] text-slate-600 shadow-brand-sm">
          <Calendar size={14} className="text-emerald-600" />
          <span>{counterLabel}</span>
          <span className="mx-1 text-emerald-200">•</span>
          <span>{branchLabel}</span>
          <span className="mx-1 text-amber-300">•</span>
          <span className="text-slate-800 font-medium">{user?.name}</span>
        </div>

        <LanguageSwitcher />

        <button
          data-testid="help-btn"
          className="hidden sm:grid h-9 w-9 place-items-center rounded-md text-slate-500 hover:bg-slate-100"
          title={t("common.help")}
        >
          <HelpCircle size={16} />
        </button>

        <Popover>
          <PopoverTrigger asChild>
            <button
              data-testid="notifications-btn"
              className="relative h-9 w-9 grid place-items-center rounded-md text-slate-500 hover:bg-slate-100"
              title={t("header.notifications")}
            >
              <Bell size={16} />
              {badgeTotal > 0 && (
                <span className="absolute -top-0.5 -right-0.5 min-w-[16px] h-4 px-1 rounded-full bg-[#DC2626] text-[9px] font-bold text-white flex items-center justify-center ring-2 ring-white">
                  {badgeTotal > 99 ? "99+" : badgeTotal}
                </span>
              )}
            </button>
          </PopoverTrigger>
          <PopoverContent align="end" className="w-80 p-0">
            <div className="px-3 py-2.5 border-b text-[13px] font-semibold flex items-center justify-between">
              {t("header.notifications")}
              <Badge variant="secondary" className="text-[10px]">
                {badgeTotal}
              </Badge>
            </div>
            {(counts.new > 0 || counts.pending_delivery > 0) && (
              <div className="flex gap-2 px-3 py-2 border-b bg-emerald-50/50 text-[11px]">
                <span className="rounded-full bg-emerald-100 px-2 py-0.5 font-semibold text-emerald-800">
                  {t("onlineOrders.newOrders")}: {counts.new || 0}
                </span>
                <span className="rounded-full bg-amber-100 px-2 py-0.5 font-semibold text-amber-800">
                  {t("onlineOrders.pendingDelivery")}: {counts.pending_delivery || 0}
                </span>
              </div>
            )}
            <ul className="divide-y max-h-72 overflow-y-auto">
              {notifications.length === 0 && (
                <li className="px-3 py-6 text-center text-[12px] text-slate-400">
                  No pending online or delivery alerts
                </li>
              )}
              {notifications.map((n) => (
                <li key={n.id}>
                  <Link to={n.to} className="block px-3 py-2.5 hover:bg-slate-50">
                    <div className="flex items-start gap-2.5">
                      <span
                        className="h-1.5 w-1.5 rounded-full mt-1.5"
                        style={{
                          background: n.type === "warn" ? "#F59E0B" : "#059669",
                        }}
                      />
                      <div className="text-[12.5px]">
                        <div className="text-slate-800">{n.title}</div>
                        <div className="text-slate-400 text-[11px] mt-0.5">{n.time}</div>
                      </div>
                    </div>
                  </Link>
                </li>
              ))}
            </ul>
            <Link
              to="/online/orders?attention=1"
              className="block border-t p-2 text-center text-[12px] text-emerald-700 hover:bg-emerald-50"
            >
              {t("header.viewAllNotifications")}
            </Link>
          </PopoverContent>
        </Popover>

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              data-testid="user-menu-btn"
              className="flex items-center gap-2 h-9 px-2 rounded-md hover:bg-slate-100"
            >
              <div className="h-7 w-7 rounded-full bg-gradient-to-br from-emerald-400 to-amber-400 grid place-items-center text-white text-[11px] font-bold shadow-sm ring-2 ring-emerald-100">
                {initials(user?.name)}
              </div>
              <div className="hidden md:flex flex-col items-start leading-tight">
                <span className="text-[12.5px] font-semibold text-slate-800">{user?.name}</span>
                <span className="text-[10.5px] text-slate-500 capitalize">
                  {user?.role || "user"} • {user?.default_counter?.name || "—"}
                </span>
              </div>
              <ChevronDown size={14} className="text-slate-400 hidden sm:block" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-56">
            <DropdownMenuLabel>{t("header.myAccount")}</DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuItem>{t("header.profile")}</DropdownMenuItem>
            <DropdownMenuItem>{t("header.changePassword")}</DropdownMenuItem>
            <DropdownMenuItem>{t("header.counterActivity")}</DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem className="text-red-600" onClick={handleLogout}>
              {t("header.logout")}
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </header>
  );
}
